import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DynamicIcon from "../DynamicIcon";
import { selectAccountData } from "../../redux/main/accountSlice";
import { createEquipment, getEquipmentDetails, selectEquipmentDetails, updateEquipment } from "../../redux/equipment/equipmentSlice";
import { resetRefreshNow, selectRightModalmode, setHasToRefresh } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";

export default function AddEditEquipment({ equipmentId }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const equipmentDetails = useSelector(selectEquipmentDetails);
    const modalMode = useSelector(selectRightModalmode);
    const token = localStorage.getItem('installamelo-auth-token');
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        duration: 0,
        active: true,
        exclusiveUse: false,
    });

    // Initialize form data based on edit mode
    useEffect(() => {
        if (modalMode === 'edit' && equipmentId) {
            setIsLoading(true);
            dispatch(getEquipmentDetails({ equipmentId, token }))
                .then(() => setIsLoading(false))
                .catch(() => {
                    toast.error("Errore nel caricamento dei dettagli dell'attrezzatura");
                    setIsLoading(false);
                });
        }
        dispatch(resetRefreshNow());
    }, [dispatch, equipmentId, modalMode, token]);

    // Populate form when equipmentDetails is loaded
    useEffect(() => {
        if (equipmentDetails && modalMode === 'edit') {
            setFormData({
                title: equipmentDetails.title || '',
                description: equipmentDetails.description || '',
                duration: equipmentDetails.duration || 0,
                active: equipmentDetails.active !== undefined ? equipmentDetails.active : true,
                exclusiveUse: equipmentDetails.exclusiveUse !== undefined ? equipmentDetails.exclusiveUse : false,
            });
        }
    }, [equipmentDetails, modalMode]);

    // Form change handler
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Form submission handler
    const handleSubmit = (e) => {
        e.preventDefault();
        
        setIsLoading(true);
        
        const equipmentData = {
            ...formData,
            accountId: accountData._id,
        };
        
        // If we're in edit mode, include the ID
        if (modalMode === 'edit' && equipmentDetails) {
            equipmentData._id = equipmentDetails._id;
        }
        
        const action = modalMode === 'add' 
            ? createEquipment({ equipment: equipmentData, token })
            : updateEquipment({ equipment: equipmentData, token });
        
        dispatch(action)
            .then(() => {
                const successMessage = modalMode === 'add' 
                    ? "Attrezzatura creata con successo" 
                    : "Attrezzatura aggiornata con successo";
                toast.success(successMessage);
                dispatch(setHasToRefresh());
                setIsLoading(false);
            })
            .catch((err) => {
                const errorMessage = modalMode === 'add' 
                    ? "Errore nella creazione dell'attrezzatura" 
                    : "Errore nell'aggiornamento dell'attrezzatura";
                toast.error(errorMessage);
                setIsLoading(false);
            });
    };

    return (
        <div className="flex flex-col h-full">
            {isLoading && <LoadingSection />}
            
            <div className="flex-1 overflow-auto p-4">
                <h2 className="text-xl font-semibold mb-4">
                    {modalMode === 'add' ? 'Nuova Attrezzatura' : 'Modifica Attrezzatura'}
                </h2>
                
                <form onSubmit={handleSubmit}>
                    {/* Titolo */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Titolo</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            required
                        />
                    </div>
                    
                    {/* Descrizione */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Descrizione</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            rows="3"
                        />
                    </div>
                    
                    {/* Durata */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700">Durata (minuti)</label>
                        <input
                            type="number"
                            name="duration"
                            value={formData.duration}
                            onChange={handleChange}
                            className="mt-1 p-2 w-full border rounded-md"
                            min="0"
                        />
                    </div>
                    
                    {/* Checkbox per active */}
                    <div className="mb-4 flex items-center">
                        <input
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleChange}
                            className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">Attrezzatura attiva</label>
                    </div>
                    
                    {/* Checkbox per exclusiveUse */}
                    <div className="mb-4 flex items-center">
                        <input
                            type="checkbox"
                            name="exclusiveUse"
                            checked={formData.exclusiveUse}
                            onChange={handleChange}
                            className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">Uso esclusivo</label>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="submit"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={isLoading}
                        >
                            <DynamicIcon iconClass="fa-solid fa-save mr-2" />
                            {modalMode === 'add' ? 'Crea' : 'Aggiorna'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
